// Physical paint controls inside the existing Paint Tool settings panel: the
// Paint Lab's painting controls (medium, brush, pigments, loading, water,
// thickness, advanced medium multipliers, brush rotation, environment).
// Rendered with the panel's own SelectRow/SliderRow so it matches the Canvas
// design; shown only when the physical engine is active. Paper & Surface lives
// in its own document-wide side panel.

import React, { useState } from 'react';
import { MEDIUM_STOPS, mediumAt } from '../materials/MediumModel.js';
import { BRUSH_PRESETS } from '../brushes/BrushPresets.js';
import { PIGMENTS } from '../pigments/PigmentLibrary.js';
import { nearestMediumStop } from './physicalPaintSettings.js';
import BrushPreviewSwatch from './BrushPreviewSwatch.js';
import ModelSurfaceControls from './ModelSurfaceControls.js';

const labelStyle = { fontSize: 10, fontWeight: 950, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' };
const chip = (active) => ({
  minHeight: 29,
  borderRadius: 8,
  border: active ? '1px solid rgba(143,211,255,0.72)' : '1px solid rgba(255,255,255,0.12)',
  background: active ? 'rgba(143,211,255,0.16)' : 'rgba(255,255,255,0.055)',
  color: active ? '#bfe8ff' : 'rgba(255,255,255,0.76)',
  fontSize: 10,
  fontWeight: 900,
  cursor: 'pointer',
  padding: '0 8px',
});
const noteStyle = { fontSize: 10, lineHeight: 1.45, color: 'rgba(255,255,255,0.5)' };

// Multiplier sliders work in hundredths (100 = x1.00).
const times = (v) => Math.round((v ?? 1) * 100);

export default function PhysicalPaintSection({ value, onChange, SelectRow, SliderRow, previewSettings = null }) {
  const [advanced, setAdvanced] = useState(false);
  // Functional updates: several quick changes must not overwrite each other.
  const set = (patch) => onChange((current) => ({ ...current, ...patch }));

  const stop = nearestMediumStop(value.medium);
  const medium = mediumAt(value.medium);
  return (
    <div style={{ display: 'grid', gap: 11, minWidth: 0, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <ModelSurfaceControls SelectRow={SelectRow} SliderRow={SliderRow} />
      <SliderRow label={`Medium · ${stop.name}`} value={Math.round(value.medium * 100)} min={0} max={100} suffix="" onChange={(v) => set({ medium: v / 100 })} />
      <div style={{ display: 'grid', gap: 6 }}>
        <span style={labelStyle}>Medium presets</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {MEDIUM_STOPS.map((m) => (
            <button key={m.id} type="button" style={chip(stop.id === m.id)} onClick={() => set({ medium: m.at })}>{m.name}</button>
          ))}
        </div>
      </div>
      <SelectRow
        label="Brush"
        value={value.brush}
        options={BRUSH_PRESETS.filter((b) => b.tool !== 'knife').map((b) => ({ id: b.id, label: b.name }))}
        onChange={(v) => set({ brush: v })}
      />
      {previewSettings ? <BrushPreviewSwatch settings={{ ...previewSettings, physical: value }} /> : null}
      <SelectRow
        label="Pigment"
        value={value.pigment}
        options={[{ id: 'picker', label: 'Colour picker' }, ...PIGMENTS.map((p) => ({ id: p.id, label: p.name }))]}
        onChange={(v) => set({ pigment: v })}
      />
      <SelectRow
        label="Mix with"
        value={value.secondPigment}
        options={[{ id: '', label: 'Nothing' }, ...PIGMENTS.map((p) => ({ id: p.id, label: p.name }))]}
        onChange={(v) => set({ secondPigment: v })}
      />
      {value.secondPigment ? <SliderRow label="Mix amount" value={Math.round(value.mix * 100)} min={0} max={100} suffix="%" onChange={(v) => set({ mix: v / 100 })} /> : null}
      <SliderRow label="Paint loading" value={Math.round(value.loading * 100)} min={0} max={100} suffix="%" onChange={(v) => set({ loading: v / 100 })} />
      <SliderRow label="Water" value={times(value.waterScale)} min={0} max={300} suffix="%" onChange={(v) => set({ waterScale: v / 100 })} />
      <SliderRow label="Thickness" value={times(value.thicknessScale)} min={0} max={300} suffix="%" onChange={(v) => set({ thicknessScale: v / 100 })} />
      <SliderRow label="Impasto depth" value={Math.round(value.impastoDepth * 100)} min={0} max={300} suffix="%" onChange={(v) => set({ impastoDepth: v / 100 })} />

      <button type="button" style={{ ...chip(advanced), justifySelf: 'start' }} onClick={() => setAdvanced((a) => !a)} aria-expanded={advanced}>
        {advanced ? 'Hide advanced paint' : 'Advanced paint'}
      </button>
      {advanced ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <SliderRow label="Pigment concentration" value={times(value.pigmentScale)} min={0} max={300} suffix="%" onChange={(v) => set({ pigmentScale: v / 100 })} />
          <SliderRow label="Fluidity" value={times(value.fluidityScale)} min={10} max={1000} suffix="%" onChange={(v) => set({ fluidityScale: v / 100 })} />
          <SliderRow label="Drying speed" value={times(value.dryingScale)} min={0} max={500} suffix="%" onChange={(v) => set({ dryingScale: v / 100 })} />
          <SliderRow label="Paint pickup" value={times(value.pickupScale)} min={0} max={200} suffix="%" onChange={(v) => set({ pickupScale: v / 100 })} />
          <SelectRow
            label="Brush rotation"
            value={value.rotationMode || 'direction'}
            options={[
              { id: 'direction', label: 'Follow stroke direction' },
              { id: 'pen', label: 'Pen barrel rotation (twist)' },
              { id: 'fixed', label: 'Fixed' },
            ]}
            onChange={(v) => set({ rotationMode: v })}
          />
          <SliderRow label="Humidity" value={Math.round((value.humidity ?? 0.4) * 100)} min={0} max={95} suffix="%" onChange={(v) => set({ humidity: v / 100 })} />
          <SliderRow label="Time speed" value={times(value.timeScale)} min={0} max={800} suffix="%" onChange={(v) => set({ timeScale: v / 100 })} />
          <div style={noteStyle}>
            Medium sets water {medium.water.toFixed(2)}, body {medium.body.toFixed(2)}, pigment {medium.pigment.toFixed(2)}, yield {medium.yieldStress.toFixed(3)}, rewet {medium.rewet.toFixed(2)}.
          </div>
        </div>
      ) : null}
    </div>
  );
}
